import api from '../../../lib/api';
import type { AiChatSessionDetail, AiChatSessionSummary } from '../shared/chatHistoryTypes';
import { downloadBlobFile, parseContentDispositionFilename } from '../shared/downloadBlobFile';
import type {
  AiInteractionDomain,
  ConfirmProposalResult,
  CopilotChatSession,
  SendCopilotMessageResult,
} from './types';

export async function listCopilotSessions(searchQuery?: string): Promise<AiChatSessionSummary[]> {
  const { data } = await api.get('/platform/ai/chat/sessions', {
    params: searchQuery?.trim() ? { q: searchQuery.trim() } : undefined,
  });
  return data.data as AiChatSessionSummary[];
}

export async function renameCopilotSession(
  sessionId: string,
  title: string,
): Promise<AiChatSessionSummary> {
  const { data } = await api.patch(`/platform/ai/chat/sessions/${sessionId}`, { title });
  return data.data as AiChatSessionSummary;
}

export async function exportCopilotSession(
  sessionId: string,
  format: 'md' | 'txt',
): Promise<void> {
  const response = await api.get(`/platform/ai/chat/sessions/${sessionId}/export`, {
    params: { format },
    responseType: 'blob',
  });
  const fallback = `conversacion-copiloto.${format}`;
  const filename = parseContentDispositionFilename(
    response.headers['content-disposition'] as string | undefined,
    fallback,
  );
  downloadBlobFile(response.data as Blob, filename);
}

export async function getCopilotSession(sessionId: string): Promise<AiChatSessionDetail> {
  const { data } = await api.get(`/platform/ai/chat/sessions/${sessionId}`);
  return data.data as AiChatSessionDetail;
}

export async function deleteCopilotSession(sessionId: string): Promise<void> {
  await api.delete(`/platform/ai/chat/sessions/${sessionId}`);
}

export async function createCopilotSession(payload?: {
  domain?: AiInteractionDomain;
  routeContext?: string;
}): Promise<CopilotChatSession> {
  const { data } = await api.post('/platform/ai/chat/sessions', payload ?? {});
  return data.data as CopilotChatSession;
}

export async function sendCopilotMessage(
  sessionId: string,
  text: string,
): Promise<SendCopilotMessageResult> {
  const { data } = await api.post(
    `/platform/ai/chat/sessions/${sessionId}/messages`,
    { text },
    { timeout: 120_000 },
  );
  return data.data as SendCopilotMessageResult;
}

export async function confirmCopilotProposal(proposalId: string): Promise<ConfirmProposalResult> {
  const { data } = await api.post(`/platform/ai/proposals/${proposalId}/confirm`);
  return data.data as ConfirmProposalResult;
}
